/**
 * WeaveIn.ai Platform - Document Upload Dialog
 * Multi-step upload flow: Category > Type > Metadata > Files > Submit
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, X, FileText, AlertCircle, Check, Loader2, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { getWeaveinApiUrl } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

interface DocCategory {
  id: number;
  name: string;
  scope: string;
}

interface MetadataField {
  field_id: string;
  field_type: string;
  label: string;
  required: boolean;
  options?: string[];
}

interface DocType {
  id: number;
  name: string;
  scope: string;
  is_notes_required: boolean;
  metadata_fields: MetadataField[];
  attribution_levels: string[];
  allowed_file_types: string[];
  max_file_size_mb: number | null;
  due_date_rules: any;
  requires_hard_copy_workflow?: boolean;
}

interface DocSubtype {
  id: number;
  name: string;
  effective_name: string | null;
  is_notes_required: boolean;
  metadata_fields: MetadataField[];
  due_date_rules: any;
}

interface Program {
  id: number;
  name: string;
}

interface Enrollment {
  id: number;
  program_id: number;
  program_name: string;
  status: string;
}

interface PendingFile {
  file: File;
  name: string;
  size: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

interface DocumentUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetType: 'member' | 'staff';
  targetId: number;
  targetName?: string;
  defaultEnrollmentId?: number;
  defaultProgramId?: number;
  /** TRUS-762: file the new document under this case. */
  caseEpisodeId?: number | null;
  onUploadComplete?: () => void;
  /**
   * 'dialog' (default) or 'page': the same uploader rendered in place, without
   * its own title, for a host screen with a header and a way back (the member's
   * Activities workspace, TRUS-762). `open` is still honoured: closed renders
   * nothing, and Cancel / done call onOpenChange(false).
   */
  variant?: 'dialog' | 'page';
}

// ============================================================================
// HELPERS
// ============================================================================

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

// ============================================================================
// COMPONENT
// ============================================================================

export function DocumentUploadDialog({
  open, onOpenChange, targetType, targetId, targetName, defaultEnrollmentId, defaultProgramId, caseEpisodeId, onUploadComplete,
  variant = 'dialog',
}: DocumentUploadDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step state
  const [categories, setCategories] = useState<DocCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [docTypesLoading, setDocTypesLoading] = useState(false);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [selectedType, setSelectedType] = useState<DocType | null>(null);
  // The third taxonomy level. Most types have none, so this stays empty and the
  // picker never appears — the pre-sub-type flow, unchanged.
  const [docSubtypes, setDocSubtypes] = useState<DocSubtype[]>([]);
  const [selectedSubtypeId, setSelectedSubtypeId] = useState<number | null>(null);

  // Attribution
  const [programs, setPrograms] = useState<Program[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(null);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<number | null>(null);

  // Metadata
  const [metadataValues, setMetadataValues] = useState<Record<string, any>>({});
  const [notes, setNotes] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [expirationDate, setExpirationDate] = useState('');

  // Files
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // State
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState<string | null>(null);

  // ========================================================================
  // DATA FETCHING
  // ========================================================================

  useEffect(() => {
    if (!open) return;
    // Reset state
    setSelectedCategoryId(null);
    setSelectedTypeId(null);
    setSelectedType(null);
    setDocSubtypes([]);
    setSelectedSubtypeId(null);
    setMetadataValues({});
    setNotes('');
    setEffectiveDate('');
    setExpirationDate('');
    setPendingFiles([]);
    setError(null);
    setSuccess(false);
    setTrackingNumber(null);
    // Apply defaults from parent (enrollment context)
    setSelectedEnrollmentId(defaultEnrollmentId || null);
    setSelectedProgramId(defaultProgramId || null);

    // Fetch categories
    fetch(getWeaveinApiUrl(`/api/doc-categories?scope=${targetType}`), {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    })
      .then((r) => r.json())
      .then(setCategories)
      .catch(() => {});

    // Fetch member programs/enrollments if member
    if (targetType === 'member') {
      fetch(getWeaveinApiUrl(`/api/members/${targetId}/enrollments?status=active`), {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      })
        .then((r) => r.ok ? r.json() : [])
        .then((data) => {
          if (Array.isArray(data)) {
            setEnrollments(data);
            const uniquePrograms = Array.from(
              new Map(data.map((e: any) => [e.program_id, { id: e.program_id, name: e.program_name || `Program ${e.program_id}` }])).values()
            );
            setPrograms(uniquePrograms as Program[]);
          }
        })
        .catch(() => {});
    }
  }, [open, targetType, targetId]);

  // Fetch doc types when category changes. Clear the previous category's types
  // up front so the Type dropdown never transiently shows a mismatched list
  // (types belong to a single category via category_id) while the fetch is in
  // flight, and track loading so an empty result is distinguishable from a
  // not-yet-loaded one (see the empty-category notice in render).
  useEffect(() => {
    setDocTypes([]);
    if (!selectedCategoryId) {
      setDocTypesLoading(false);
      return;
    }
    // Standard fetch-in-effect cleanup, as hardening rather than a fix for an
    // observed defect: clearing up front handles the common case, but nothing
    // otherwise stops a slower fetch for category A from resolving after the
    // user has already moved to B and repopulating the dropdown with A's types
    // (or flipping loading off while B is still in flight). Not reproducible in
    // jsdom, so it carries no test — the ignore flag just makes it impossible.
    let cancelled = false;
    setDocTypesLoading(true);
    // Filter to the types available to the ambient program when the screen has
    // one — the enrollment Documents tab passes defaultProgramId. A type with no
    // program restriction comes back either way; only a type an admin has scoped
    // to specific programs is withheld.
    //
    // Deliberately keyed off defaultProgramId, NOT the selectedProgramId picker
    // below: that picker is only rendered for a type that accepts 'program'
    // attribution, so filtering the type list by it would be circular — you
    // could not pick the program until you had picked a type.
    const programFilter = defaultProgramId ? `&program_id=${defaultProgramId}` : '';
    fetch(getWeaveinApiUrl(`/api/doc-types?category_id=${selectedCategoryId}&scope=${targetType}${programFilter}`), {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    })
      .then((r) => r.json())
      .then((data) => {
        if (!cancelled) setDocTypes(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setDocTypes([]);
      })
      .finally(() => {
        if (!cancelled) setDocTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCategoryId, targetType, defaultProgramId]);

  // Update selected type details
  useEffect(() => {
    const dt = docTypes.find((t) => t.id === selectedTypeId) || null;
    setSelectedType(dt);
    setMetadataValues({});
  }, [selectedTypeId, docTypes]);

  // Clear captured values when the sub-type changes: the field set changes with
  // it, and a value keyed to a field that is no longer rendered would be posted
  // invisibly.
  useEffect(() => {
    setMetadataValues({});
  }, [selectedSubtypeId]);

  // Fetch the chosen type's sub-types, resolved for the ambient program. The
  // program matters twice over: it decides WHICH sub-types are offered, and what
  // this program calls each one (effective_name).
  useEffect(() => {
    setDocSubtypes([]);
    setSelectedSubtypeId(null);
    if (!selectedTypeId) return;

    let cancelled = false;
    const programFilter = defaultProgramId ? `&program_id=${defaultProgramId}` : '';
    fetch(getWeaveinApiUrl(`/api/doc-subtypes?doc_type_id=${selectedTypeId}${programFilter}`), {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (!cancelled) setDocSubtypes(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        // A type with no sub-level is the norm, and so is the empty list this
        // leaves behind: the picker stays hidden and the upload proceeds against
        // the type alone. Failing the whole dialog over it would be worse.
        if (!cancelled) setDocSubtypes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedTypeId, defaultProgramId]);

  const selectedSubtype = docSubtypes.find((st) => st.id === selectedSubtypeId) || null;

  // Both levels can carry a metadata schema, so the form shows the union. A
  // sub-type field with the same field_id REPLACES the type's rather than
  // rendering twice — that is how a sub-type narrows a shared field (e.g. making
  // an optional account number required).
  const effectiveMetadataFields: MetadataField[] = React.useMemo(() => {
    const merged = new Map<string, MetadataField>();
    (selectedType?.metadata_fields || []).forEach((f) => merged.set(f.field_id, f));
    (selectedSubtype?.metadata_fields || []).forEach((f) => merged.set(f.field_id, f));
    return Array.from(merged.values());
  }, [selectedType, selectedSubtype]);

  // A category whose type catalog has loaded and is empty. Uploading here posts
  // category_id instead of doc_type_id and the server resolves a default type
  // for the category, so the file area, dates, notes and submit must un-gate
  // even though no type can be selected (TRUS-436). A category that HAS types is
  // unaffected — one must still be chosen.
  const typelessCategory =
    selectedCategoryId !== null && !docTypesLoading && docTypes.length === 0;

  // Everything downstream of the type/category choice keys off this rather than
  // selectedType, so the two paths stay in step.
  const readyForFiles = selectedType !== null || typelessCategory;

  // ========================================================================
  // FILE HANDLING
  // ========================================================================

  const addFiles = (files: File[]) => {
    if (files.length === 0) return;
    const newPending: PendingFile[] = files.map((f) => ({
      file: f, name: f.name, size: f.size, status: 'pending',
    }));
    setPendingFiles((prev) => [...prev, ...newPending]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(Array.from(e.target.files || []));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Drag-and-drop mirrors the click path. Without an onDrop/onDragOver handler
  // the browser falls back to its default action — navigating away to open the
  // dropped file ("this file type is not supported"). preventDefault() on both
  // keeps the drop inside the dropzone and hands the files to addFiles().
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // ========================================================================
  // SUBMIT
  // ========================================================================

  const handleSubmit = async () => {
    if (!readyForFiles || pendingFiles.length === 0) return;

    // A type-less category has no metadata schema and no notes requirement to
    // enforce — both live on the doc type. Skip rather than guard each access.
    if (selectedType) {
      for (const field of effectiveMetadataFields) {
        if (field.required && !metadataValues[field.field_id]) {
          setError(`"${field.label}" is required`);
          return;
        }
      }

      // A sub-type can ADD the notes requirement but never waive its type's,
      // matching the server's check.
      if ((selectedType.is_notes_required || selectedSubtype?.is_notes_required) && !notes.trim()) {
        setError('Notes are required for this document type');
        return;
      }
    }

    setSubmitting(true);
    setError(null);

    try {
      // Step 1: Create document record
      const createRes = await fetch(getWeaveinApiUrl('/api/documents'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          // Exactly one of these — the API rejects both or neither.
          ...(selectedType
            ? { doc_type_id: selectedType.id }
            : { category_id: selectedCategoryId }),
          ...(selectedSubtypeId ? { doc_subtype_id: selectedSubtypeId } : {}),
          target_type: targetType,
          target_id: targetId,
          program_id: selectedProgramId,
          enrollment_id: selectedEnrollmentId,
          ...(caseEpisodeId ? { case_episode_id: caseEpisodeId } : {}),
          metadata_values: metadataValues,
          notes: notes || null,
          effective_date: effectiveDate || null,
          expiration_date: expirationDate || null,
        }),
      });

      if (!createRes.ok) {
        const err = await createRes.json();
        throw new Error(err.detail || 'Failed to create document');
      }

      const doc = await createRes.json();

      // Step 2: Upload files
      const formData = new FormData();
      pendingFiles.forEach((pf) => formData.append('files', pf.file));

      setPendingFiles((prev) =>
        prev.map((pf) => ({ ...pf, status: 'uploading' as const }))
      );

      const uploadRes = await fetch(getWeaveinApiUrl(`/api/documents/${doc.id}/files`), {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!uploadRes.ok) {
        throw new Error('File upload failed');
      }

      const uploadResult = await uploadRes.json();

      // Update file statuses
      setPendingFiles((prev) =>
        prev.map((pf, i) => {
          const fileResult = uploadResult.files?.[i];
          return {
            ...pf,
            status: fileResult?.success ? 'success' : 'error',
            error: fileResult?.error,
          };
        })
      );

      setSuccess(true);
      onUploadComplete?.();

      // If hard-copy workflow enabled, auto-create tracking record
      if (selectedType?.requires_hard_copy_workflow && doc.id) {
        try {
          const trackRes = await fetch(getWeaveinApiUrl('/api/hard-copy-tracking'), {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({
              doc_record_id: doc.id,
              target_type: targetType,
              target_id: targetId,
              program_id: selectedProgramId || undefined,
              enrollment_id: selectedEnrollmentId || undefined,
            }),
          });
          if (trackRes.ok) {
            const trackData = await trackRes.json();
            setTrackingNumber(trackData.tracking_number);
          }
        } catch {
          // Non-blocking: tracking creation failure doesn't affect upload
        }
      }

      // Auto-close after brief delay (longer if tracking number shown)
      setTimeout(() => onOpenChange(false), selectedType?.requires_hard_copy_workflow ? 3000 : 1500);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================================================
  // RENDER
  // ========================================================================

  // File rules live on the doc type, so a type-less upload has none to read.
  // This mirrors the server's own fallback (documents.py upload_document_files
  // defaults to the same list, which is also the doc_types.allowed_file_types
  // column default the auto-created type inherits) — so what we advertise here
  // is what the API will actually accept.
  const DEFAULT_ACCEPTED_FILE_TYPES = ['pdf', 'jpg', 'jpeg', 'png', 'heic'];
  const acceptedFileTypes = selectedType?.allowed_file_types?.length
    ? selectedType.allowed_file_types
    : DEFAULT_ACCEPTED_FILE_TYPES;
  const acceptTypes = acceptedFileTypes.map((t) => `.${t}`).join(',');

  const body = (
      <>
        {success ? (
          <div className="flex flex-col items-center py-8">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <Check className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-lg font-medium">Document uploaded successfully</p>
            {trackingNumber && (
              <div className="mt-3 text-center">
                <p className="text-sm text-muted-foreground">Hard-copy tracking number:</p>
                <p className="font-mono text-lg font-bold text-primary mt-1">{trackingNumber}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Print this number on the physical document for tracking.
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Category Selection */}
            <div>
              <Label>Document Category *</Label>
              <Select
                value={selectedCategoryId?.toString() || ''}
                onValueChange={(v) => {
                  setSelectedCategoryId(parseInt(v));
                  setSelectedTypeId(null);
                }}
              >
                <SelectTrigger><SelectValue placeholder="Select a category..." /></SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Type Selection — hidden for a category with no types.
                TRUS-464 configures IFR with Document Category as the ONLY
                classification field, which the platform expresses as a category
                carrying no doc_types (TRUS-436): the server then resolves a hidden
                default type on upload. Without this guard the dialog still renders
                "Document Type *" over an empty dropdown the user cannot satisfy. */}
            {selectedCategoryId && docTypes.length > 0 && (
              <div>
                <Label>Document Type *</Label>
                <Select
                  value={selectedTypeId?.toString() || ''}
                  onValueChange={(v) => setSelectedTypeId(parseInt(v))}
                >
                  <SelectTrigger><SelectValue placeholder="Select a type..." /></SelectTrigger>
                  <SelectContent>
                    {docTypes.map((dt) => (
                      <SelectItem key={dt.id} value={dt.id.toString()}>{dt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Sub-type Selection. Rendered only when the chosen type actually
                has sub-types available here — most do not, and an empty picker
                would read as a step the user had failed to complete. Optional by
                design: doc_records.doc_subtype_id is nullable, so a document can
                be filed against the type alone. */}
            {selectedType && docSubtypes.length > 0 && (
              <div>
                <Label>{selectedType.name} Sub-type</Label>
                <Select
                  value={selectedSubtypeId?.toString() || ''}
                  onValueChange={(v) => setSelectedSubtypeId(v ? parseInt(v) : null)}
                >
                  <SelectTrigger><SelectValue placeholder="Select a sub-type (optional)..." /></SelectTrigger>
                  <SelectContent>
                    {docSubtypes.map((st) => (
                      <SelectItem key={st.id} value={st.id.toString()}>
                        {st.effective_name || st.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* This category has no document types, so there is nothing to pick
                and the Type dropdown above is empty. The upload still goes
                through — it is filed under the category itself (TRUS-436). Say
                so plainly; an earlier version of this notice told the user the
                upload was impossible, which it no longer is. */}
            {typelessCategory && (
              <div className="flex items-start gap-2 text-sm p-3 rounded-md bg-muted text-muted-foreground">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  No document types are set up for this category, so there&apos;s
                  nothing to choose above. Your upload will be filed under
                  &ldquo;{categories.find((c) => c.id === selectedCategoryId)?.name}&rdquo;
                  without a type.
                </span>
              </div>
            )}

            {/* Due Date Info */}
            {selectedType?.due_date_rules?.type !== 'none' && selectedType?.due_date_rules?.type && (
              <div className="text-sm p-2 bg-amber-50 text-amber-800 rounded-md">
                Due date: {selectedType.due_date_rules.type === 'relative'
                  ? `${selectedType.due_date_rules.days_after_enrollment} days after enrollment`
                  : selectedType.due_date_rules.fixed_date || 'Configured'}
              </div>
            )}

            {/* Attribution Selection */}
            {selectedType && selectedType.attribution_levels.includes('program') && programs.length > 0 && (
              <div>
                <Label>Program</Label>
                <Select
                  value={selectedProgramId?.toString() || ''}
                  onValueChange={(v) => setSelectedProgramId(v ? parseInt(v) : null)}
                >
                  <SelectTrigger><SelectValue placeholder="Select program..." /></SelectTrigger>
                  <SelectContent>
                    {programs.map((p) => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedType && selectedType.attribution_levels.includes('enrollment') && enrollments.length > 0 && (
              <div>
                <Label>Enrollment</Label>
                <Select
                  value={selectedEnrollmentId?.toString() || ''}
                  onValueChange={(v) => setSelectedEnrollmentId(v ? parseInt(v) : null)}
                >
                  <SelectTrigger><SelectValue placeholder="Select enrollment..." /></SelectTrigger>
                  <SelectContent>
                    {enrollments
                      .filter((e) => !selectedProgramId || e.program_id === selectedProgramId)
                      .map((e) => (
                        <SelectItem key={e.id} value={e.id.toString()}>
                          {e.program_name} (#{e.id})
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Dynamic Metadata Fields — the type's schema plus the selected
                sub-type's, which may narrow a shared field. */}
            {effectiveMetadataFields.map((field) => (
              <div key={field.field_id}>
                <Label>{field.label} {field.required && '*'}</Label>
                {field.field_type === 'date' ? (
                  <Input
                    type="date"
                    value={metadataValues[field.field_id] || ''}
                    onChange={(e) =>
                      setMetadataValues({ ...metadataValues, [field.field_id]: e.target.value })
                    }
                  />
                ) : field.field_type === 'dropdown' && field.options ? (
                  <Select
                    value={metadataValues[field.field_id] || ''}
                    onValueChange={(v) =>
                      setMetadataValues({ ...metadataValues, [field.field_id]: v })
                    }
                  >
                    <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {field.options.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : field.field_type === 'textarea' ? (
                  <Textarea
                    value={metadataValues[field.field_id] || ''}
                    onChange={(e) =>
                      setMetadataValues({ ...metadataValues, [field.field_id]: e.target.value })
                    }
                  />
                ) : field.field_type === 'checkbox' ? (
                  <div className="flex items-center gap-2 mt-1">
                    <Checkbox
                      checked={!!metadataValues[field.field_id]}
                      onCheckedChange={(v) =>
                        setMetadataValues({ ...metadataValues, [field.field_id]: !!v })
                      }
                    />
                    <span className="text-sm">{field.label}</span>
                  </div>
                ) : (
                  <Input
                    type={field.field_type === 'number' ? 'number' : 'text'}
                    value={metadataValues[field.field_id] || ''}
                    onChange={(e) =>
                      setMetadataValues({ ...metadataValues, [field.field_id]: e.target.value })
                    }
                  />
                )}
              </div>
            ))}

            {/* Dates */}
            {readyForFiles && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={effectiveDate}
                    onChange={(e) => setEffectiveDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Expiration Date</Label>
                  <Input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            {readyForFiles && (
              <div>
                <Label>Notes {selectedType?.is_notes_required && '*'}</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
                  placeholder="Add notes about this document..."
                  maxLength={2000}
                />
                <span className="text-xs text-muted-foreground">{notes.length}/2000</span>
              </div>
            )}

            {/* File Upload Area */}
            {readyForFiles && (
              <div>
                <Label>Files *</Label>
                <div
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDragging ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    Click to select files or drag and drop
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Accepted: {acceptedFileTypes.join(', ').toUpperCase()}
                    {selectedType?.max_file_size_mb && ` | Max ${selectedType.max_file_size_mb} MB`}
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={acceptTypes}
                  onChange={handleFileSelect}
                  className="hidden"
                />

                {/* File List */}
                {pendingFiles.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {pendingFiles.map((pf, i) => (
                      <div key={i} className="flex items-center justify-between p-2 border rounded-md text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                          <span className="truncate">{pf.name}</span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">
                            {formatFileSize(pf.size)}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {pf.status === 'uploading' && <Loader2 className="w-4 h-4 animate-spin" />}
                          {pf.status === 'success' && <Check className="w-4 h-4 text-green-600" />}
                          {pf.status === 'error' && (
                            <span className="text-xs text-destructive">{pf.error || 'Error'}</span>
                          )}
                          {pf.status === 'pending' && (
                            <Button variant="ghost" size="sm" onClick={() => removeFile(i)}>
                              <Trash2 className="w-3 h-3 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!success && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!readyForFiles || pendingFiles.length === 0 || submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" /> Upload Document
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </>
  );

  if (variant === 'page') {
    if (!open) return null;
    return <div className="rounded-lg border border-v2border bg-surface p-4 sm:p-6 space-y-4">{body}</div>;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>
            {targetName ? `For ${targetName}` : `For ${targetType} #${targetId}`}
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}
